/**
 * Unified Google Services Registry - Phase 4 Architecture
 *
 * Consolidates all Google service registrations:
 * - Calendar (+ Business Hours, Booking Types, Availability Calculator)
 * - Gmail
 * - Google Docs
 * - Google Tasks
 *
 * Standardizes registration pattern and provides unified health checks
 */

import { serviceRegistry } from "@/services/core/service-registry";
import { logger } from "@/lib/logger";

// Calendar Ecosystem
import { AvailabilityCalculatorService } from "./availability-calculator.service";
import { BookingTypesService } from "./booking-types.service";
import { BusinessHoursService } from "./business-hours.service";
import { calendarAuthService } from "./core/calendar-auth.service";
import { calendarEventsService } from "./core/calendar-events.service";

// Gmail
import { gmailAuthService } from "./core/gmail-auth.service";
import { gmailClientService } from "./core/gmail-client.service";

// Google Docs
import { googleDocsService } from "./core/google-docs.service";

// Gemini AI (OAuth-based Gemini API access)
import { geminiAuthService } from "./core/gemini-auth.service";

// Google Tasks
import { googleTasksAuthService } from './core/google-tasks-auth.service';
import { GoogleTasksSyncService } from './core/google-tasks-sync.service';
import { GoogleTasksApiClient } from './core/google-tasks-api-client.service';
import { GoogleTasksListService } from './core/google-tasks-list.service';
import { GoogleTasksTaskService } from './core/google-tasks-task.service';
import { GoogleTasksSyncEngine } from './core/google-tasks-sync-engine.service';
import { GoogleTasksConflictService } from './core/google-tasks-conflict.service';
import type { GoogleTasksAuthService } from './core/google-tasks-auth.service';

// Google Contacts
import { googleContactsAuthService } from './core/google-contacts-auth.service';
import { GoogleContactsSyncService } from './core/google-contacts-sync.service';
import type { GoogleContactsAuthService } from './core/google-contacts-auth.service';

const log = logger.createNamespace('GoogleServicesRegistry');

/**
 * Register all Google services with the service registry
 * Call this during application initialization
 */
export function registerAllGoogleServices(): void {
  log.info('Registering all Google services');

  // ========================================
  // Calendar Ecosystem
  // ========================================

  // Business Hours (Foundation)
  serviceRegistry.register("business-hours", () => new BusinessHoursService(), {
    features: {
      timezoneSupport: true,
      holidaySupport: true,
      flexibleScheduling: true,
      melbourneTimezone: true,
    },
    limits: {
      maxBusinessDays: 7,
      earliestHour: 6,
      latestHour: 22,
    },
  });

  // Calendar Auth Service
  serviceRegistry.register(
    "calendar-auth",
    () => calendarAuthService,
    {
      features: {
        oauth: true,
        pkce: true,
        tokenManagement: true,
        rateLimit: true,
      },
      limits: {
        maxOAuthAttemptsPerHour: 10,
        pkceVerifierExpiryMinutes: 10,
      },
    }
  );

  // Calendar Events Service
  serviceRegistry.register(
    "calendar-events",
    () => calendarEventsService,
    {
      features: {
        eventCreation: true,
        meetLinks: true,
        conferenceData: true,
        fallbackStrategy: true,
      },
      limits: {
        maxEventsPerDay: 50,
        maxDurationMinutes: 240,
        minDurationMinutes: 15,
      },
      endpoints: {
        calendar: "https://www.googleapis.com/calendar/v3",
      },
    }
  );

  // Booking Types Service
  serviceRegistry.register("booking-types", () => new BookingTypesService(), {
    features: {
      autoApproval: true,
      tieredApproval: true,
      smartScheduling: true,
      validation: true,
    },
    limits: {
      maxBookingTypes: 10,
      maxDurationMinutes: 240,
      minDurationMinutes: 15,
    },
  });

  // Availability Calculator Service
  serviceRegistry.register(
    "availability-calculator",
    () => new AvailabilityCalculatorService(),
    {
      features: {
        complexCalculations: true,
        timezoneSupport: true,
        conflictDetection: true,
        bufferManagement: true,
        slotGeneration: true,
      },
      limits: {
        maxDaysToSearch: 30,
        slotIntervalMinutes: 30,
        bufferMinutes: 15,
        maxSlotsPerDay: 32,
      },
    }
  );

  // ========================================
  // Gmail Services
  // ========================================

  serviceRegistry.register(
    "gmail-auth",
    () => gmailAuthService,
    {
      features: {
        oauth: true,
        pkce: true,
        tokenManagement: true,
        rateLimit: true,
      },
      limits: {
        maxOAuthAttemptsPerHour: 10,
        pkceVerifierExpiryMinutes: 10,
      },
    }
  );

  serviceRegistry.register(
    "gmail-client",
    () => gmailClientService,
    {
      features: {
        sendEmail: true,
        receiveEmail: true,
        searchEmail: true,
        incrementalSync: true,
        attachments: true,
        labels: true,
      },
      limits: {
        maxEmailsPerSync: 1000,
        maxAttachmentSizeMB: 25,
      },
      endpoints: {
        gmail: "https://gmail.googleapis.com/gmail/v1",
      },
    }
  );

  // ========================================
  // Google Docs Service
  // ========================================

  serviceRegistry.register(
    "google-docs",
    () => googleDocsService,
    {
      features: {
        createDocument: true,
        updateDocument: true,
        exportDocument: true,
        revisionHistory: true,
        collaboration: true,
      },
      limits: {
        maxDocumentsPerSync: 500,
      },
      endpoints: {
        docs: "https://docs.googleapis.com/v1",
        drive: "https://www.googleapis.com/drive/v3",
      },
    }
  );

  // ========================================
  // Gemini AI (OAuth-based API Access)
  // ========================================

  serviceRegistry.register(
    "gemini-auth",
    () => geminiAuthService,
    {
      features: {
        oauth: true,
        pkce: true,
        tokenManagement: true,
        rateLimit: true,
        generativeLanguage: true,
      },
      limits: {
        maxOAuthAttemptsPerHour: 10,
        pkceVerifierExpiryMinutes: 10,
      },
      endpoints: {
        generativeLanguage: "https://generativelanguage.googleapis.com/v1beta",
      },
    }
  );

  // ========================================
  // Google Tasks Services
  // ========================================

  serviceRegistry.register(
    'google-tasks-auth',
    () => googleTasksAuthService,
    {
      features: {
        oauth: true,
        tokenManagement: true,
        calendarFallback: true,
      },
    }
  );

  // Google Tasks sub-services
  serviceRegistry.register(
    'google-tasks-api-client',
    async () => {
      const apiClient = new GoogleTasksApiClient();
      const authService = await serviceRegistry.get<GoogleTasksAuthService>('google-tasks-auth');
      apiClient.setAuthService(authService);
      return apiClient;
    },
    {
      features: {
        rateLimit: true,
        httpClient: true,
      },
    }
  );

  serviceRegistry.register(
    'google-tasks-list',
    async () => {
      const apiClient = await serviceRegistry.get<GoogleTasksApiClient>('google-tasks-api-client');
      return new GoogleTasksListService(apiClient);
    },
    {
      features: {
        taskListCrud: true,
        scopeValidation: true,
      },
    }
  );

  serviceRegistry.register(
    'google-tasks-task',
    async () => {
      const apiClient = await serviceRegistry.get<GoogleTasksApiClient>('google-tasks-api-client');
      const listService = await serviceRegistry.get<GoogleTasksListService>('google-tasks-list');
      return new GoogleTasksTaskService(apiClient, listService);
    },
    {
      features: {
        taskCrud: true,
        lookups: true,
      },
    }
  );

  serviceRegistry.register(
    'google-tasks-sync-engine',
    async () => {
      const apiClient = await serviceRegistry.get<GoogleTasksApiClient>('google-tasks-api-client');
      const listService = await serviceRegistry.get<GoogleTasksListService>('google-tasks-list');
      const taskService = await serviceRegistry.get<GoogleTasksTaskService>('google-tasks-task');
      return new GoogleTasksSyncEngine(apiClient, listService, taskService);
    },
    {
      features: {
        bidirectionalSync: true,
        incrementalSync: true,
      },
    }
  );

  serviceRegistry.register(
    'google-tasks-conflict',
    async () => {
      const apiClient = await serviceRegistry.get<GoogleTasksApiClient>('google-tasks-api-client');
      const listService = await serviceRegistry.get<GoogleTasksListService>('google-tasks-list');
      const taskService = await serviceRegistry.get<GoogleTasksTaskService>('google-tasks-task');
      const syncEngine = await serviceRegistry.get<GoogleTasksSyncEngine>('google-tasks-sync-engine');
      return new GoogleTasksConflictService(apiClient, listService, taskService, syncEngine);
    },
    {
      features: {
        conflictDetection: true,
        conflictResolution: true,
      },
    }
  );

  serviceRegistry.register(
    'google-tasks-sync',
    async () => {
      const authService = await serviceRegistry.get<GoogleTasksAuthService>('google-tasks-auth');
      const apiClient = await serviceRegistry.get<GoogleTasksApiClient>('google-tasks-api-client');
      const listService = await serviceRegistry.get<GoogleTasksListService>('google-tasks-list');
      const taskService = await serviceRegistry.get<GoogleTasksTaskService>('google-tasks-task');
      const syncEngine = await serviceRegistry.get<GoogleTasksSyncEngine>('google-tasks-sync-engine');
      const conflictService = await serviceRegistry.get<GoogleTasksConflictService>('google-tasks-conflict');
      const syncService = new GoogleTasksSyncService({ apiClient, listService, taskService, syncEngine, conflictService });
      syncService.setAuthService(authService);
      return syncService;
    },
    {
      features: {
        bidirectionalSync: true,
        userAssignmentFiltering: true,
        conflictResolution: true,
      },
    }
  );

  // ========================================
  // Google Contacts Services
  // ========================================

  serviceRegistry.register(
    'google-contacts-auth',
    () => googleContactsAuthService,
    {
      features: {
        oauth: true,
        tokenManagement: true,
        peopleApi: true,
      },
    }
  );

  serviceRegistry.register(
    'google-contacts-sync',
    () => {
      const syncService = new GoogleContactsSyncService();
      syncService.setAuthService(googleContactsAuthService);
      return syncService;
    },
    {
      features: {
        bidirectionalSync: true,
        emailDeduplication: true,
        peopleApi: true,
      },
    }
  );

  log.success('All Google services registered');
  log.info('✅ Calendar Ecosystem (5 services): Auth, Events, Business Hours, Booking Types, Availability');
  log.info('✅ Gmail (2 services): Auth, Client');
  log.info('✅ Google Docs (1 service): Document Management');
  log.info('✅ Gemini AI (1 service): OAuth Auth');
  log.info('✅ Google Tasks (7 services): Auth, API Client, List, Task, Sync Engine, Conflict, Sync');
  log.info('✅ Google Contacts (2 services): Auth, Sync');
  log.info('📊 Total: 18 Google services registered');
}

// ========================================
// Helper Functions - Calendar
// ========================================

export const getCalendarAuthService = () =>
  serviceRegistry.get("calendar-auth");

export const getCalendarEventsService = () =>
  serviceRegistry.get("calendar-events");

export const getBookingTypesService = () =>
  serviceRegistry.get<BookingTypesService>("booking-types");

export const getBusinessHoursService = () =>
  serviceRegistry.get<BusinessHoursService>("business-hours");

export const getAvailabilityCalculatorService = () =>
  serviceRegistry.get<AvailabilityCalculatorService>("availability-calculator");

// ========================================
// Helper Functions - Gmail
// ========================================

export const getGmailAuthService = () =>
  serviceRegistry.get("gmail-auth");

export const getGmailClientService = () =>
  serviceRegistry.get("gmail-client");

// ========================================
// Helper Functions - Google Docs
// ========================================

export const getGoogleDocsService = () =>
  serviceRegistry.get("google-docs");

// ========================================
// Helper Functions - Gemini AI
// ========================================

export const getGeminiAuthService = () =>
  serviceRegistry.get("gemini-auth");

// ========================================
// Helper Functions - Google Tasks
// ========================================

export const getGoogleTasksAuthService = () =>
  serviceRegistry.get<GoogleTasksAuthService>('google-tasks-auth');

export const getGoogleTasksSyncService = () =>
  serviceRegistry.get<GoogleTasksSyncService>('google-tasks-sync');

// ========================================
// Helper Functions - Google Contacts
// ========================================

export const getGoogleContactsAuthService = () =>
  serviceRegistry.get<GoogleContactsAuthService>('google-contacts-auth');

export const getGoogleContactsSyncService = () =>
  serviceRegistry.get<GoogleContactsSyncService>('google-contacts-sync');

// ========================================
// Ecosystem Resolution
// ========================================

/**
 * Calendar Ecosystem Dependency Resolution Helper
 * Resolves all calendar services in proper dependency order
 */
export const resolveCalendarEcosystem = async () => {
  // Resolve in dependency order: Foundation → Configuration → Core → Terminal
  const businessHours = await getBusinessHoursService();
  const bookingTypes = await getBookingTypesService();
  const calendarAuth = await getCalendarAuthService();
  const calendarEvents = await getCalendarEventsService();
  const availabilityCalculator = await getAvailabilityCalculatorService();

  return {
    businessHours,
    bookingTypes,
    calendarAuth,
    calendarEvents,
    availabilityCalculator,
  };
};

/**
 * Google Ecosystem Health Check
 * Validates all services are properly registered and initialized
 */
export const checkGoogleEcosystemHealth = (): boolean => {
  const services = [
    // Calendar
    "business-hours",
    "calendar-auth",
    "calendar-events",
    "booking-types",
    "availability-calculator",
    // Gmail
    "gmail-auth",
    "gmail-client",
    // Google Docs
    "google-docs",
    // Gemini AI
    "gemini-auth",
    // Google Tasks
    "google-tasks-auth",
    "google-tasks-api-client",
    "google-tasks-list",
    "google-tasks-task",
    "google-tasks-sync-engine",
    "google-tasks-conflict",
    "google-tasks-sync",
    // Google Contacts
    "google-contacts-auth",
    "google-contacts-sync",
  ];

  const healthResults = services.map((service) => {
    const isRegistered = serviceRegistry.has(service);
    const isInitialized = serviceRegistry.isInitialized(service);
    log.info(
      `📊 ${service}: registered=${isRegistered}, initialized=${isInitialized}`
    );
    return isRegistered;
  });

  const allHealthy = healthResults.every(result => result);

  if (allHealthy) {
    log.success('✅ All Google services are healthy');
  } else {
    log.error('❌ Some Google services are unhealthy');
  }

  return allHealthy;
};

// Maintain backward compatibility with old function names
export const registerGoogleCalendarServices = registerAllGoogleServices;
export const checkCalendarEcosystemHealth = checkGoogleEcosystemHealth;
